import { createFeatureSelector, createSelector } from '@ngrx/store';

import { isOneTimeRegistryDomainPrice } from '../../utils/cart-domain-suggestion-rules';

import { CartState, CartItem } from './cart.state';

function cartDisplayUnitPrice(item: CartItem, price: number): number {
  if (isOneTimeRegistryDomainPrice(item.product?.uuid, item.selectedVariation?.uuid)) {
    return price;
  }
  const paymentRecurrence =
    item.selectedVariation?.paymentRecurrence ?? item.product?.paymentRecurrence;
  return paymentRecurrence === 'annual' ? price / 12 : price;
}

export const selectCartState = createFeatureSelector<CartState>('cart');

// Basic selectors
export const selectCartItems = createSelector(
  selectCartState,
  (state: CartState) => state.items
);

export const selectCartUI = createSelector(
  selectCartState,
  (state: CartState) => state.ui
);

export const selectCartSync = createSelector(
  selectCartState,
  (state: CartState) => state.sync
);

export const selectCartError = createSelector(
  selectCartState,
  (state: CartState) => state.error
);

export const selectCartLastUpdated = createSelector(
  selectCartState,
  (state: CartState) => state.lastUpdated
);

// Computed selectors
export const selectCartItemCount = createSelector(
  selectCartItems,
  (items: CartItem[]) => items.reduce((total, item) => total + item.quantity, 0)
);

export const selectCartTotal = createSelector(
  selectCartItems,
  (items: CartItem[]) => items.reduce((total, item) => {
    const price = item.selectedVariation?.salesPrice || item.product.salesPrice;
    const adjustedPrice = cartDisplayUnitPrice(item, price);
    return total + (adjustedPrice * item.quantity);
  }, 0)
);

export interface CartDisplayedTotal {
  amount: number;
  showMonthlySuffix: boolean;
  hasRegistryDomain: boolean;
  firstMonth: number;
  monthly: number;
}

/** Com registro de domínio: primeiro mês soma tudo; depois, só a mensalidade. */
export const selectCartDisplayedTotal = createSelector(
  selectCartItems,
  (items: CartItem[]): CartDisplayedTotal => {
    let monthly = 0;
    let oneTime = 0;

    for (const item of items) {
      const price = item.selectedVariation?.salesPrice || item.product.salesPrice || 0;
      const qty = item.quantity ?? 1;
      if (isOneTimeRegistryDomainPrice(item.product?.uuid, item.selectedVariation?.uuid)) {
        oneTime += price * qty;
      } else {
        monthly += cartDisplayUnitPrice(item, price) * qty;
      }
    }

    const hasRegistryDomain = oneTime > 0;
    if (hasRegistryDomain) {
      const firstMonth = monthly + oneTime;
      return {
        amount: firstMonth,
        showMonthlySuffix: false,
        hasRegistryDomain: true,
        firstMonth,
        monthly,
      };
    }

    return {
      amount: monthly,
      showMonthlySuffix: monthly > 0,
      hasRegistryDomain: false,
      firstMonth: monthly,
      monthly,
    };
  }
);

export const selectCartSubtotal = createSelector(
  selectCartItems,
  (items: CartItem[]) => items.reduce((total, item) => {
    const price = item.selectedVariation?.price || item.product.price;
    const adjustedPrice = cartDisplayUnitPrice(item, price);
    return total + (adjustedPrice * item.quantity);
  }, 0)
);

export const selectCartDiscount = createSelector(
  selectCartSubtotal,
  selectCartTotal,
  (subtotal, total) => subtotal - total
);

export const selectIsCartEmpty = createSelector(
  selectCartItems,
  (items: CartItem[]) => items.length === 0
);

// UI selectors
export const selectCartLoading = createSelector(
  selectCartUI,
  (ui) => ui.loading
);

export const selectUrlCartLoading = createSelector(
  selectCartUI,
  (ui) => ui.urlCartLoading
);

export const selectCartSidebarOpen = createSelector(
  selectCartUI,
  (ui) => ui.isSidebarOpen
);

export const selectCartSidebarHeader = createSelector(
  selectCartUI,
  (ui) => ui.sidebarHeader
);

export const selectCartHeaderWithCount = createSelector(
  selectCartSidebarHeader,
  selectCartItemCount,
  (header, count) => {
    const titleKey = count < 2 
      ? 'components.cart.headerTitleSingular' 
      : 'components.cart.headerTitlePlural';
    return { 
      ...header, 
      title: '', 
      titleKey: titleKey,
      titleParams: { count } 
    };
  }
);

// Sync selectors
export const selectSyncInProgress = createSelector(
  selectCartSync,
  (sync) => sync.inProgress
);

export const selectLastSync = createSelector(
  selectCartSync,
  (sync) => sync.lastSync
);

export const selectSyncError = createSelector(
  selectCartSync,
  (sync) => sync.lastError
);

export const selectSyncRetryCount = createSelector(
  selectCartSync,
  (sync) => sync.retryCount
);

// Item-specific selectors
export const selectCartItemById = (itemId: string) => 
  createSelector(
    selectCartItems,
    (items: CartItem[]) => items.find(item => item.id === itemId)
  );

export const selectCartItemQuantity = (itemId: string) =>
  createSelector(
    selectCartItemById(itemId),
    (item) => item?.quantity || 0
  );

// Advanced selectors
export const selectCartItemsByCategory = (category: string) =>
  createSelector(
    selectCartItems,
    (items: CartItem[]) => items.filter(item => 
      item.product.categories?.some(cat => cat.name === category)
    )
  );

export const selectCartItemsByProductType = (productType: string) =>
  createSelector(
    selectCartItems,
    (items: CartItem[]) => items.filter(item =>
      (item.productType ?? item.product.productType) === productType
    )
  );

export const selectCartItemsWithVariations = createSelector(
  selectCartItems,
  (items: CartItem[]) => items.filter(item => item.selectedVariation)
);

export const selectCartItemsWithoutVariations = createSelector(
  selectCartItems,
  (items: CartItem[]) => items.filter(item => !item.selectedVariation)
);

// Validation selectors
export const selectCartValidationStatus = createSelector(
  selectCartItems,
  (items: CartItem[]) => {
    const totalItems = items.length;
    const itemsWithVariations = items.filter(item => item.selectedVariation).length;
    const itemsWithoutPrice = items.filter(item => {
      const price = item.selectedVariation?.salesPrice || item.product.salesPrice;
      return !price || price <= 0;
    }).length;

    const warnings: { key: string; params?: any }[] = [];
    
    if (itemsWithoutPrice > 0) {
      warnings.push({
        key: 'components.cart.validation.noPrice',
        params: { count: itemsWithoutPrice }
      });
    }
    
    return {
      totalItems,
      itemsWithVariations,
      itemsWithoutPrice,
      isValid: itemsWithoutPrice === 0,
      warnings
    };
  }
);

// Performance selectors
export const selectCartStats = createSelector(
  selectCartItems,
  selectCartItemCount,
  selectCartTotal,
  selectCartLastUpdated,
  (items, count, total, lastUpdated) => ({
    itemCount: items.length,
    totalQuantity: count,
    totalValue: total,
    lastUpdated,
    averageItemValue: items.length > 0 ? total / items.length : 0
  })
);
